import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Icon from '../Icon';
import TranslationProvider from '../../providers/TranslationProvider';

import Chevron from '../../svg/icons/caret.svg';
import './Dropdown.scss';

const Dropdown = ({
  name,
  options,
  selected,
  onChange,
  onClick,
  formatLabel,
  autocomplete,
  errors,
  theme,
  disabled,
  icon,
  style,
  ariaRequired,
  ariaLabel,
  showChevron,
  isCodeFreezeFeatureFlag,
}) => {
  const [selectedValue, setSelectedValue] = useState(selected || options[0]?.value);

  useEffect(() => {
    // Update parent state on mount
    onChange({ [name]: selectedValue });
  }, []);

  useEffect(() => {
    if (selected !== selectedValue) {
      setSelectedValue(selected);
      if (isCodeFreezeFeatureFlag) {
        onChange({ [name]: selected });
      }
    }
  }, [selected]);

  const handleChange = (e) => {
    const value = e.target?.selectedOptions?.[0]?.value || e.target.value;
    setSelectedValue(value);
    onChange({ [name]: value });
  };

  const getStylizedLabel = (key) => {
    const selectedOption = options.find((option) => option.value === key) || options[0];
    return formatLabel
      ? selectedOption && formatLabel(selectedOption)
      : selectedOption?.label?.text || selectedOption?.label;
  };

  const renderOptions = () => options.map((eachOption) => {
    if (!eachOption) {
      return null;
    }

    if (eachOption.label?.text) {
      return (
        <option
          key={eachOption.key != null ? eachOption.key : eachOption.value}
          value={eachOption.value}
          label={eachOption.label.text}
        >
          <TranslationProvider translationKey={eachOption.label.translationKey}>
            <span>{eachOption.label.text}</span>
          </TranslationProvider>
        </option>
      );
    }

    // option does not need to be translated
    return (
      <option
        key={eachOption.key != null ? eachOption.key : eachOption.value}
        value={eachOption.value}
        label={eachOption.label.text}
      >
        {eachOption.label}
      </option>
    );
  });

  const invalid = errors.indexOf(name) !== -1;

  return (
    <div className="Dropdown">
      <select
        id={name}
        style={style}
        name={name}
        onFocus={onClick}
        onChange={handleChange}
        value={selectedValue || undefined}
        autoComplete={autocomplete}
        disabled={disabled}
        aria-required={ariaRequired}
        aria-label={ariaLabel}
      >
        {renderOptions()}
      </select>
      <div
        style={style}
        data-testid={`${name}-stylizedElement`}
        className={cx('Dropdown__Stylized', {
          'Dropdown__Stylized--theme-nav': theme === 'nav',
          'Dropdown__Stylized--error': invalid,
          'Dropdown__Stylized--disabled': disabled,
        })}
      >
        {icon && (
        <Icon component={icon} iconClass="ticket" />
        )}
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        {isCodeFreezeFeatureFlag ? <label htmlFor={name}>{getStylizedLabel(selectedValue)}</label> : <label htmlFor={name}>{getStylizedLabel(selected)}</label>}

        {showChevron && (
        <Chevron
          transform="rotate(180)"
          className="chevron"
        />
        )}
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      key: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ text: PropTypes.string })]),
    })
  ).isRequired,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  formatLabel: PropTypes.func,
  autocomplete: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  theme: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  style: PropTypes.shape({
    fontWeight: PropTypes.string,
  }),
  ariaRequired: PropTypes.string,
  ariaLabel: PropTypes.string,
  showChevron: PropTypes.bool,
  isCodeFreezeFeatureFlag: PropTypes.bool,
};

Dropdown.defaultProps = {
  selected: undefined,
  formatLabel: undefined,
  autocomplete: undefined,
  errors: [],
  theme: undefined,
  onClick: () => { },
  disabled: false,
  icon: undefined,
  style: undefined,
  ariaRequired: undefined,
  ariaLabel: null,
  showChevron: true,
  isCodeFreezeFeatureFlag: false,
};

Dropdown.displayName = 'Dropdown';
export default Dropdown;
